import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import { BaselineData, PaneRendererCustomData, PriceToCoordinateConverter, Time } from 'lightweight-charts'
import { BaselineSeriesOptions } from './options'
import { ISeriesPaneRenderer } from '../customSeries.typings.ts'
import Chart from '../../chart'
import tinycolor from 'tinycolor2'

export type BaselineBarData = {
    x: number,
    value: number
}

export class BaseLineSeriesRenderer<TData extends BaselineData>
    implements ISeriesPaneRenderer {
        chart: Chart
    _data: PaneRendererCustomData<Time, TData> | null = null
    _options: BaselineSeriesOptions | null = null

    constructor(chart: Chart) {
        this.chart = chart;
    }
    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter
    ): void {
        target.useBitmapCoordinateSpace(scope =>
            this._drawImpl(scope, priceConverter)
        )
    }

    update(
        data: PaneRendererCustomData<Time, TData>,
        options: BaselineSeriesOptions
    ): void {
        this._data = data
        this._options = options
    }

    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }

        const srs = this.chart.getBlankSeries();
        if (!srs) return;
        
        const ctx = renderingScope.context;
        const pixelRatio = window.devicePixelRatio;
        const mediaWidth = renderingScope.mediaSize.width * pixelRatio;
        
        const startPrice = srs.coordinateToPrice(0) || 0
        const endPrice = srs.coordinateToPrice(renderingScope.mediaSize.height) || 0

        const low = Math.min(startPrice, endPrice)
        const high = Math.max(startPrice, endPrice)
        
        // For screener charts, use baseValue as actual price instead of percentage
        let midPoint: number;
        if (this.chart.screenerChart && this._options.baseValue >= 1) {
           midPoint = this._options.baseValue;
        } else {
           midPoint = low + (high - low) * this._options.baseValue/100;
        }
        
        const scaledMidPoint = (priceToCoordinate(midPoint) || 0) * pixelRatio
        const priceLineWidth =
            (typeof this._options.lineWidth === 'number' && this._options.lineWidth > 0
                ? this._options.lineWidth
                : 2) * pixelRatio

        // Draw midpoint line
        ctx.lineWidth = Math.max(1, priceLineWidth * 0.65)
        ctx.beginPath();
        ctx.moveTo(0, scaledMidPoint)
        ctx.lineTo(mediaWidth, scaledMidPoint);
        ctx.strokeStyle = "#afafaf";
        ctx.setLineDash([4 * pixelRatio])
        ctx.stroke();
        ctx.closePath();
        
        const {from, to} = this._data.visibleRange
        const renderLength = to - from;
        
        // Pre-convert colors to avoid repeated conversions
        const topLineColor = this._options.topLineColor;
        const bottomLineColor = this._options.bottomLineColor;
        const topFillColor1 = tinycolor(this._options.topFillColor1).toRgbString();
        const topFillColor2 = tinycolor(this._options.topFillColor2).toRgbString();
        const bottomFillColor1 = tinycolor(this._options.bottomFillColor1).toRgbString();
        const bottomFillColor2 = tinycolor(this._options.bottomFillColor2).toRgbString();
        
        // Batch segments by color to reduce draw calls
        const segments: Array<{
            path: Path2D;
            linePath: Path2D;
            color: string;
            type: 'top' | 'bottom';
            startX: number;
        }> = [];
        
        let currentSegment: {
            path: Path2D;
            linePath: Path2D;
            color: string;
            type: 'top' | 'bottom';
            startX: number;
        } | null = null;
        
        ctx.setLineDash([])
        ctx.lineWidth = priceLineWidth

        let previousValue: BaselineBarData | null = null;
        
        for (let c = 0; c <= renderLength - 1; c++) {
            const bar = this._data.bars[c + from];
            const value = bar.originalData.value;
            const currentY = priceToCoordinate(bar.originalData.value)
            const scaledBarX = bar.x * pixelRatio;
            const scaledCurrentY = currentY ? currentY * pixelRatio : null;
            
            if (!scaledCurrentY) continue;
            
            if (!previousValue) {
                const isAbove = value > midPoint;
                const color = isAbove ? topLineColor : bottomLineColor;
                const type = isAbove ? 'top' : 'bottom';
                
                currentSegment = {
                    path: new Path2D(),
                    linePath: new Path2D(),
                    color,
                    type,
                    startX: scaledBarX
                };
                
                currentSegment.path.moveTo(scaledBarX, scaledMidPoint);
                currentSegment.path.lineTo(scaledBarX, scaledCurrentY);
                currentSegment.linePath.moveTo(scaledBarX, scaledCurrentY);
                
                previousValue = { x: scaledBarX, value };
                continue;
            }
            
            const lower = Math.min(previousValue.value, value);
            const higher = Math.max(previousValue.value, value);
            const crossesMidpoint = midPoint >= lower && midPoint <= higher;
            
            if (crossesMidpoint) {
                // Close current segment at crossover point
                const crossoverX = previousValue.x + (scaledBarX - previousValue.x) * Math.abs((midPoint - previousValue.value) / (value - previousValue.value));
                
                if (currentSegment) {
                    currentSegment.linePath.lineTo(crossoverX, scaledMidPoint);
                    currentSegment.path.lineTo(crossoverX, scaledMidPoint);
                    currentSegment.path.lineTo(currentSegment.startX, scaledMidPoint);
                    segments.push(currentSegment);
                }
                
                // Start new segment after crossover
                const isAbove = value > midPoint;
                const color = isAbove ? topLineColor : bottomLineColor;
                const type = isAbove ? 'top' : 'bottom';
                
                currentSegment = {
                    path: new Path2D(),
                    linePath: new Path2D(),
                    color,
                    type,
                    startX: crossoverX
                };
                
                currentSegment.path.moveTo(crossoverX, scaledMidPoint);
                currentSegment.path.lineTo(scaledBarX, scaledCurrentY);
                currentSegment.linePath.moveTo(crossoverX, scaledMidPoint);
                currentSegment.linePath.lineTo(scaledBarX, scaledCurrentY);
            } else {
                // Continue current segment
                if (currentSegment) {
                    currentSegment.linePath.lineTo(scaledBarX, scaledCurrentY);
                    currentSegment.path.lineTo(scaledBarX, scaledCurrentY);
                }
            }
            
            previousValue = { x: scaledBarX, value };
        }
        
        // Close final segment
        if (currentSegment && previousValue) {
            // Don't draw vertical line to midpoint on the line path
            // Only close the fill path
            currentSegment.path.lineTo(previousValue.x, scaledMidPoint);
            currentSegment.path.lineTo(currentSegment.startX, scaledMidPoint);
            segments.push(currentSegment);
        }
        
        // Draw all segments in batches
        for (const segment of segments) {
            // Draw fill
            this.fillSegment(renderingScope, ctx, segment.path, scaledMidPoint, segment.type, topFillColor1, topFillColor2, bottomFillColor1, bottomFillColor2);
            
            // Draw line
            ctx.strokeStyle = segment.color;
            ctx.stroke(segment.linePath);
        }
    }

    fillSegment(renderingScope: BitmapCoordinatesRenderingScope, ctx: CanvasRenderingContext2D, path: Path2D, midPoint: number, type: "top" | "bottom", topFillColor1: string, topFillColor2: string, bottomFillColor1: string, bottomFillColor2: string) {
        path.closePath();
        ctx.globalAlpha = 1;
        let gradient: CanvasGradient;
        const mediaHeight = renderingScope.mediaSize.height * window.devicePixelRatio;
        
        if (type === "bottom") {
            gradient = ctx.createLinearGradient(0, midPoint, 0, mediaHeight);
            gradient.addColorStop(0, bottomFillColor1)
            gradient.addColorStop(1, bottomFillColor2)
        } else {
            gradient = ctx.createLinearGradient(0, midPoint, 0, 0);
            gradient.addColorStop(0, topFillColor1)
            gradient.addColorStop(1, topFillColor2)
        }
        
        ctx.fillStyle = gradient
        ctx.fill(path)
    }
}
